import { useEffect, useMemo, useState } from 'react';
import { MapPin } from 'lucide-react';
import { apiGet, type Game, type MapSection } from '../lib/api';
import StadiumMap from '../components/StadiumMap';
import SectionPanel from '../components/SectionPanel';
import { demoGames, demoSectionAvailability, useDemoMode } from '../lib/demo';
import { levelOf, LEVEL_NAME, type LevelId } from '../lib/metlife';

export default function MapPage() {
  const [games, setGames] = useState<Game[]>([]);
  const [gameId, setGameId] = useState('');
  const [apiSections, setApiSections] = useState<MapSection[]>([]);
  const [active, setActive] = useState<{ section: string; query: string } | null>(null);
  const [demoOn] = useDemoMode();

  useEffect(() => {
    let cancelled = false;
    apiGet<{ games: Game[] }>('/games')
      .catch(() => ({ games: [] as Game[] }))
      .then(({ games }) => {
        if (cancelled) return;
        const list = games.length ? games : demoOn ? demoGames() : [];
        setGames(list);
        setGameId((cur) =>
          cur && list.some((g) => g.game_id === cur) ? cur : list[0]?.game_id ?? ''
        );
      });
    return () => {
      cancelled = true;
    };
  }, [demoOn]);

  useEffect(() => {
    if (!gameId) {
      setApiSections([]);
      return;
    }
    let cancelled = false;
    apiGet<{ sections: MapSection[] }>(`/map/availability?game_id=${gameId}`)
      .catch(() => ({ sections: [] as MapSection[] }))
      .then(({ sections }) => !cancelled && setApiSections(sections));
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  // Real availability wins; demo fills in sections that have no real listings.
  const sections = useMemo(() => {
    if (!demoOn || !gameId) return apiSections;
    const real = new Set(apiSections.map((s) => s.section.trim().toUpperCase()));
    const demo = demoSectionAvailability(gameId).filter((s) => !real.has(s.section));
    return [...apiSections, ...demo];
  }, [apiSections, demoOn, gameId]);

  const game = games.find((g) => g.game_id === gameId);

  return (
    <section>
      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <div className="flex-1 w-full min-w-0">
          <div className="card rounded-xl p-6">
            <h2 className="!mb-1">MetLife Stadium</h2>
            <p className="text-sm muted mt-0 mb-5">
              NY Giants — East Rutherford, NJ · Select a section to browse listings
            </p>

            <label>
              <span>Game</span>
              <select value={gameId} onChange={(e) => setGameId(e.target.value)}>
                {games.map((g) => (
                  <option key={g.game_id} value={g.game_id}>
                    {g.opponent} — {g.game_date}
                  </option>
                ))}
              </select>
            </label>
            {!games.length && (
              <p className="muted">
                No games scheduled yet. Turn on "Demo data" in the top bar to explore the map
                with sample listings.
              </p>
            )}

            <StadiumMap
              sections={sections}
              selectedSection={active?.section}
              onSectionClick={(section, query) => setActive({ section, query })}
            />
          </div>
        </div>

        <div className="w-full lg:w-96 shrink-0 flex flex-col gap-4">
          {active && gameId ? (
            <SectionPanel
              section={active.section}
              querySection={active.query}
              gameId={gameId}
              game={game}
              onClose={() => setActive(null)}
            />
          ) : (
            <div className="card rounded-xl p-5">
              <div className="text-center py-10">
                <MapPin size={32} className="mx-auto text-ink-300 mb-3" />
                <p className="text-sm font-semibold muted m-0">Select a section</p>
                <p className="text-xs text-giants-gray mt-1 mb-0">
                  Click any area on the map to see available listings
                </p>
              </div>
            </div>
          )}

          <PriceTiers sections={sections} game={game} />
        </div>
      </div>
    </section>
  );
}

/** Per-level price guide computed from live availability. */
function PriceTiers({ sections, game }: { sections: MapSection[]; game?: Game }) {
  const tiers = useMemo(() => {
    const byLevel = new Map<LevelId, { min: number; max: number }>();
    for (const s of sections) {
      const level = levelOf(s.section);
      if (!level) continue;
      const cur = byLevel.get(level);
      byLevel.set(level, {
        min: Math.min(cur?.min ?? Infinity, s.min_price_cents),
        max: Math.max(cur?.max ?? 0, s.min_price_cents),
      });
    }
    return (['lower', 'mezz', 'upper'] as LevelId[])
      .filter((l) => byLevel.has(l))
      .map((l) => ({ level: l, ...byLevel.get(l)! }));
  }, [sections]);

  if (!tiers.length) return null;

  const swatch: Record<LevelId, string> = {
    lower: '#0b2265',
    mezz: '#2e5fd4',
    upper: '#6b8fd4',
  };

  return (
    <div className="card rounded-xl p-5">
      <h4 className="text-sm font-bold text-ink-900 mt-0 mb-3">
        Price guide{game ? ` · ${game.opponent}` : ''}
      </h4>
      <div className="flex flex-col gap-2">
        {tiers.map((t) => (
          <div key={t.level} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-xs inline-block" style={{ background: swatch[t.level] }} />
              <span className="muted">{LEVEL_NAME[t.level]}</span>
            </div>
            <span className="font-semibold text-ink-900 tabular-nums">
              {t.min === t.max
                ? `$${Math.round(t.min / 100)}`
                : `$${Math.round(t.min / 100)}–$${Math.round(t.max / 100)}`}
            </span>
          </div>
        ))}
      </div>
      <p className="text-[11px] muted mt-3 mb-0">Cheapest listing per section, by level.</p>
    </div>
  );
}
